import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { DashboardFilters } from './useDashboardFilters';

// Generic dashboard filter toolbar. Driven by the hook output —
// the chip / sort slug sets travel through `filters` itself, so
// the toolbar doesn't need to know which surface it's rendering
// for. Translation keys live under the configurable prefix; both
// the director and player namespaces follow `<prefix>.status_chip.*`,
// `<prefix>.time_chip.*`, `<prefix>.sort.*`, etc.
//
// Style notes (from story 079 iterations):
//
//  - Hover / :active are scoped to data-[state=off] so they can't
//    override the active state's primary color.
//  - Hover is gated by (hover: hover) AND (pointer: fine) so phones
//    that misreport hover-capable still can't trigger sticky hover.
//  - The chip is keyed off the data-state attribute; the active vs
//    inactive styles layer rather than swap.

interface Props {
  filters: DashboardFilters<unknown>;
  /** i18next namespace, e.g. 'director' or 'player'. */
  i18nNamespace: string;
  /** Key prefix beneath the namespace, e.g. 'dashboard.toolbar'. */
  i18nPrefix: string;
}

const HOVER_FINE = '[@media(hover:hover)and(pointer:fine)]';

function Chip({
  testId,
  active,
  label,
  count,
  countTestId,
  onClick,
}: {
  testId: string;
  active: boolean;
  label: string;
  count: number;
  countTestId: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      data-state={active ? 'on' : 'off'}
      onClick={onClick}
      className={[
        'inline-flex items-center gap-1.5 rounded-full border border-input bg-background',
        'px-3 py-1 text-sm transition-colors',
        'data-[state=on]:border-primary data-[state=on]:bg-primary data-[state=on]:text-primary-foreground',
        `data-[state=off]:${HOVER_FINE}:hover:bg-accent`,
        `data-[state=off]:${HOVER_FINE}:hover:text-accent-foreground`,
        'data-[state=off]:active:bg-accent/70',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      ].join(' ')}
    >
      <span>{label}</span>
      <span
        data-testid={countTestId}
        className={[
          'rounded-full px-1.5 text-xs',
          active
            ? 'bg-primary-foreground/20 text-primary-foreground'
            : 'bg-muted text-muted-foreground',
        ].join(' ')}
      >
        {count}
      </span>
    </button>
  );
}

export function DashboardFilterToolbar({
  filters,
  i18nNamespace,
  i18nPrefix,
}: Props) {
  const { t } = useTranslation(i18nNamespace);
  const statusSet = new Set(filters.selectedStatuses);
  const timeSet = new Set(filters.selectedTimes);

  const activeCount =
    filters.selectedStatuses.length +
    filters.selectedTimes.length +
    (filters.search ? 1 : 0) +
    (filters.sort !== 'default' ? 1 : 0);

  const [open, setOpen] = useState<boolean>(activeCount > 0);

  return (
    <div
      data-testid="dashboard-filter-toolbar"
      className="mb-4 rounded-lg border bg-card"
    >
      <button
        type="button"
        data-testid="dashboard-toolbar-toggle"
        aria-expanded={open}
        aria-controls="dashboard-toolbar-body"
        onClick={() => setOpen((v) => !v)}
        className={[
          'flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-medium',
          `${HOVER_FINE}:hover:bg-accent/30`,
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        ].join(' ')}
      >
        <span className="flex items-center gap-2">
          <span>{t(`${i18nPrefix}.title`)}</span>
          {activeCount > 0 && (
            <span
              data-testid="dashboard-toolbar-active-count"
              className="rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground"
            >
              {activeCount}
            </span>
          )}
        </span>
        <ChevronDown
          className={'h-4 w-4 transition-transform ' + (open ? 'rotate-180' : '')}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          id="dashboard-toolbar-body"
          data-testid="dashboard-toolbar-body"
          className="space-y-3 border-t px-3 py-3"
        >
          <div className="flex flex-wrap gap-2">
            {filters.statusChips.map((chip) => (
              <Chip
                key={chip}
                testId={`filter-chip-status-${chip}`}
                countTestId={`filter-chip-status-${chip}-count`}
                active={statusSet.has(chip)}
                label={t(`${i18nPrefix}.status_chip.${chip}`)}
                count={filters.statusCounts[chip] ?? 0}
                onClick={() => filters.toggleStatus(chip)}
              />
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {filters.timeChips.map((chip) => (
              <Chip
                key={chip}
                testId={`filter-chip-time-${chip}`}
                countTestId={`filter-chip-time-${chip}-count`}
                active={timeSet.has(chip)}
                label={t(`${i18nPrefix}.time_chip.${chip}`)}
                count={filters.timeCounts[chip] ?? 0}
                onClick={() => filters.toggleTime(chip)}
              />
            ))}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              data-testid="dashboard-search-input"
              className="sm:flex-1"
              type="search"
              value={filters.search}
              onChange={(e) => filters.setSearch(e.target.value)}
              placeholder={t(`${i18nPrefix}.search_placeholder`)}
            />
            <Select
              value={filters.sort}
              onValueChange={(v) => filters.setSort(v)}
            >
              <SelectTrigger
                data-testid="dashboard-sort-select"
                className="sm:w-56"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {filters.sortOptions.map((opt) => (
                  <SelectItem
                    key={opt}
                    value={opt}
                    data-testid={`dashboard-sort-option-${opt}`}
                  >
                    {t(`${i18nPrefix}.sort.${opt}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
}
